import type { Direction } from './direction';
import type { SimLevel } from './level';
import type { Intent, MowerSpec } from './mower';
import { Simulation, type CarryOver, type LoggedIntent, type SimStatus } from './simulation';
import { BOOST_IDS } from './boosts';
import { ITEM_IDS } from './items';
import type { SimRules } from './simRules';
import type { ToolSpec } from './tools';

/**
 * Everything needed to reproduce a run exactly: the level, the mower, its tools, what it carried in
 * from the lawn before, and every intent stamped with the tick it applied on. Self-contained (the
 * level is embedded) so it survives content changes.
 */
export interface Replay {
  version: 1;
  level: SimLevel;
  mower: MowerSpec;
  /** The loadout, in slot order (absent: none). */
  tools?: ToolSpec[];
  /** Health and fuel carried in (an Endless run; absent: a fresh start). */
  carry?: CarryOver;
  intents: LoggedIntent[];
  /** Tick the recording stopped at. */
  endTick: number;
  /** The rules it was played by (absent: the game's own, src/sim/simRules.ts). */
  rules?: Readonly<SimRules>;
}

export function recordReplay(sim: Simulation): Replay {
  const carry = { ...sim.carry };
  return {
    version: 1,
    level: sim.level,
    mower: sim.mowerSpec,
    ...(sim.tools.length > 0 ? { tools: sim.tools.map((t) => ({ ...t })) } : {}),
    ...(Object.keys(carry).length > 0 ? { carry } : {}),
    intents: sim.intentLog.map(({ tick, intent }) => ({ tick, intent: { ...intent } })),
    endTick: sim.tick,
  };
}

/**
 * Re-runs a replay headlessly and returns the simulation in its final state. `onInput` sees each
 * intent just before it is entered, with the sim as it was when the player made it; `onStep` sees
 * the sim after every tick.
 */
export function playReplay(
  replay: Replay,
  onInput?: (sim: Readonly<Simulation>, intent: Intent) => void,
  onStep?: (sim: Readonly<Simulation>) => void,
): Simulation {
  const sim = new Simulation(
    replay.level,
    replay.mower,
    replay.carry ?? {},
    replay.tools ?? [],
    replay.rules,
  );
  let next = 0;
  const intents = [...replay.intents].sort((a, b) => a.tick - b.tick);
  const reviveNext = () => intents[next]?.intent.type === 'revive';
  while (sim.tick < replay.endTick && (sim.status === 'running' || reviveNext())) {
    const tick = sim.tick + 1;
    while (next < intents.length && (intents[next] as LoggedIntent).tick <= tick) {
      const { intent } = intents[next] as LoggedIntent;
      onInput?.(sim, intent);
      sim.enqueue(intent);
      next++;
    }
    sim.step();
    onStep?.(sim);
  }
  return sim;
}

const STATUS_CODE: Record<SimStatus, number> = {
  running: 0,
  won: 1,
  broken: 2,
  outOfFuel: 3,
  caught: 4,
  outmowed: 5,
  late: 6,
};
const LAST_TURN_CODE = { left: 1, right: 2 } as const;

function intentCode(intent: Intent | null): number {
  if (!intent) return 0;
  if (intent.type === 'face') return 10 + intent.dir;
  if (intent.type === 'throttle') return intent.slow ? 31 : 30;
  if (intent.type === 'tool') return 40 + intent.slot;
  if (intent.type === 'revive') return 50;
  if (intent.type === 'boost') return 60 + BOOST_IDS.indexOf(intent.boost);
  if (intent.type === 'park') return 70;
  if (intent.type === 'item') return 80 + ITEM_IDS.indexOf(intent.item);
  return { left: 20, right: 21, reverse: 22 }[intent.turn];
}

/**
 * An intent as one small number (0 is "none"), for compact recordings: runs sent to the server and
 * test fixtures. The codes are part of the rules: never renumber one, only add.
 */
export function encodeIntent(intent: Intent): number {
  return intentCode(intent);
}

const TURN_BY_CODE = { 20: 'left', 21: 'right', 22: 'reverse' } as const;

/** The intent an {@link encodeIntent} code stands for. Throws on a code it does not know. */
export function decodeIntent(code: number): Intent {
  if (code >= 10 && code <= 13) return { type: 'face', dir: (code - 10) as Direction };
  if (code === 20 || code === 21 || code === 22) return { type: 'turn', turn: TURN_BY_CODE[code] };
  if (code === 30 || code === 31) return { type: 'throttle', slow: code === 31 };
  if (code >= 40 && code < 50) return { type: 'tool', slot: code - 40 };
  if (code === 50) return { type: 'revive' };
  const boost = BOOST_IDS[code - 60];
  if (code >= 60 && boost !== undefined) return { type: 'boost', boost };
  if (code === 70) return { type: 'park' };
  const item = ITEM_IDS[code - 80];
  if (code >= 80 && item !== undefined) return { type: 'item', item };
  throw new RangeError(`unknown intent code ${code}`);
}

/**
 * 32-bit FNV-1a hash over all gameplay-relevant state. Two runs with the same hash ended in the
 * same state. Used by golden tests to catch unintended behavior changes.
 */
export function hashSimState(sim: Simulation): string {
  let h = 0x811c9dc5;
  const mix = (n: number) => {
    // Hash each value as 4 little-endian bytes.
    for (let shift = 0; shift < 32; shift += 8) {
      h ^= (n >>> shift) & 0xff;
      h = Math.imul(h, 0x01000193);
    }
  };
  const m = sim.mower;
  [
    sim.tick,
    STATUS_CODE[sim.status],
    m.x,
    m.y,
    m.dir,
    m.progress,
    m.hp,
    m.invulnTicks,
    m.lastTurn ? LAST_TURN_CODE[m.lastTurn] : 0,
    m.tilesSinceTurn,
    m.pullbackCooldown,
    intentCode(m.pendingIntent),
    intentCode(m.queuedIntent),
    m.departed ? 1 : 0,
    m.intentCentersLeft,
    Number.isFinite(m.fuel) ? m.fuel : -1,
    m.slow ? 1 : 0,
    m.parked ? 1 : 0,
    m.parkRequested ? 1 : 0,
    m.parkTicks,
    ...sim.tools.flatMap((_, i) => [
      sim.toolState(i)?.charges ?? 0,
      sim.toolState(i)?.cooldown ?? 0,
    ]),
    m.side === 'right' ? 1 : 0,
    sim.coins,
    sim.spareCoins,
    sim.trampled,
    sim.smashed,
    sim.chewed,
    sim.dogBumps,
    sim.sprinklersBroken,
    ...(sim.level.pickups ?? []).map((_, i) => (sim.isCollected(i) ? 1 : 0)),
    ...sim.critters.list.flatMap((c) => [
      c.x,
      c.y,
      c.dir,
      c.timer,
      c.hopsLeft,
      c.alarmed ? 1 : 0,
      c.inside ? 1 : 0,
      c.walkLeft,
      c.homeward ? 1 : 0,
      c.penned ? 1 : 0,
      c.awayTicks,
    ]),
    ...(sim.rival
      ? [sim.rival.x, sim.rival.y, sim.rival.dir, sim.rival.progress, sim.rival.tiles]
      : []),
    ...BOOST_IDS.flatMap((b) => [sim.boostTicks(b), sim.boostUsed(b) ? 1 : 0]),
    // Item boxes: only on lawns that have them, so every other run hashes as before.
    ...(sim.hasItems ? [...ITEM_IDS.map((id) => sim.heldItem(id)), sim.trimWait] : []),
    ...(sim.moles
      ? [
          sim.moles.raised,
          sim.moles.timer,
          sim.moles.warn?.x ?? -1,
          sim.moles.warn?.y ?? -1,
          sim.moles.warn?.left ?? -1,
        ]
      : []),
  ].forEach(mix);
  // Head to head: every seat's mower and takings (nothing more on a lawn without seats).
  if (sim.seats > 1) {
    for (let seat = 0; seat < sim.seats; seat++) {
      const s = sim.mowerOf(seat)!;
      [
        s.x,
        s.y,
        s.dir,
        s.progress,
        s.hp,
        s.invulnTicks,
        s.lastTurn ? LAST_TURN_CODE[s.lastTurn] : 0,
        s.tilesSinceTurn,
        s.pullbackCooldown,
        intentCode(s.pendingIntent),
        intentCode(s.queuedIntent),
        s.departed ? 1 : 0,
        s.intentCentersLeft,
        Number.isFinite(s.fuel) ? s.fuel : -1,
        s.slow ? 1 : 0,
        s.brink ? 1 : 0,
        STATUS_CODE[sim.seatStatus(seat)],
        sim.seatTiles(seat),
        sim.seatCoins(seat),
        sim.seatDizzy(seat),
        sim.seatSteady(seat),
      ].forEach(mix);
    }
    // A soccer match: the ball and each seat's goals.
    const ball = sim.pitchBall;
    if (ball) [ball.x, ball.y, ball.dir, ball.left, ball.progress, ...sim.soccerGoals].forEach(mix);
    // A round-up: the sheep in each seat's pen (where each sheep stands is hashed above).
    sim.penCounts.forEach(mix);
  }
  for (const t of sim.grid.rawTiles()) mix(t);
  for (const s of sim.grid.rawStripes()) mix(s);
  return (h >>> 0).toString(16).padStart(8, '0');
}
